import { LoaderCircle, Sparkles } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';

import {
  createIntentKey,
  useCreateGenerationMutation,
  useCreationCapabilitiesQuery,
  useProjectsQuery,
} from '../../api/index.js';
import { useWorkspace } from '../../app/runtime.js';
import { Button } from '../../design-system/index.js';
import { createDraftScope, readCreateDraft } from './create-draft.js';
import {
  buildCreateGenerationRequest,
  parseCreationFields,
  validateCreationFields,
} from './create-page.js';

export type QuickCreateController = {
  available: boolean;
  isPending: boolean;
  run: () => Promise<void>;
};

const QuickCreateContext = createContext<QuickCreateController | null>(null);

export function useQuickCreate(): QuickCreateController {
  const controller = useContext(QuickCreateContext);
  if (!controller) throw new Error('Quick Create must be used inside QuickCreateBoundary.');
  return controller;
}

export function QuickCreateBoundary() {
  const { connection, library } = useWorkspace();
  const capabilities = useCreationCapabilitiesQuery(connection.id);
  const projects = useProjectsQuery({ status: 'active', limit: 100 });
  const create = useCreateGenerationMutation();
  const [announcement, setAnnouncement] = useState('');
  const available =
    connection.status === 'active' &&
    Boolean(capabilities.data) &&
    Boolean(projects.data) &&
    !capabilities.error &&
    !projects.error;

  const run = useCallback(async () => {
    setAnnouncement('');
    const capability = capabilities.data;
    if (!capability || !projects.data || connection.status !== 'active') {
      setAnnouncement('Quick Create is unavailable.');
      return;
    }

    const scope = createDraftScope(connection.id, library.id);
    const draft = readCreateDraft(scope, parseCreationFields);
    const fields = draft ? validateCreationFields(draft.fields, capability) : null;
    if (!draft || !fields) {
      setAnnouncement('Complete the Create settings before using Quick Create.');
      return;
    }

    const projectId = projects.data.items.some(
      (project) => project.status === 'active' && project.id === draft.projectId,
    )
      ? draft.projectId
      : null;

    try {
      await create.mutateAsync({
        body: buildCreateGenerationRequest(fields, connection.id, projectId),
        idempotencyKey: createIntentKey(),
      });
      setAnnouncement('Creation started.');
    } catch {
      setAnnouncement('Creation failed. Check the Create settings and try again.');
    }
  }, [capabilities.data, connection.id, connection.status, create, library.id, projects.data]);

  const controller = useMemo(
    () => ({ available, isPending: create.isPending, run }),
    [available, create.isPending, run],
  );

  return (
    <QuickCreateContext.Provider value={controller}>
      <Outlet />
      <p className="sr-only" role="status" aria-live="polite">
        {announcement}
      </p>
    </QuickCreateContext.Provider>
  );
}

export function QuickCreateButton({
  className,
  iconClassName = 'size-5',
}: {
  className?: string;
  iconClassName?: string;
}) {
  const quickCreate = useQuickCreate();
  const label = quickCreate.isPending
    ? 'Creating from current settings'
    : quickCreate.available
      ? 'Quick create from current settings'
      : 'Quick create unavailable';

  return (
    <Button
      variant="ghost"
      size="icon"
      className={className}
      disabled={!quickCreate.available || quickCreate.isPending}
      onClick={() => void quickCreate.run()}
      aria-label={label}
      aria-busy={quickCreate.isPending || undefined}
      title={label}
    >
      {quickCreate.isPending ? (
        <LoaderCircle
          className={`${iconClassName} animate-spin motion-reduce:animate-none`}
          aria-hidden
        />
      ) : (
        <Sparkles className={iconClassName} aria-hidden />
      )}
    </Button>
  );
}
